import type { WithRequired } from '@openx/types/helpers.ts';
import type { OptionStateMap } from '@openx/types/options.ts';
import { useEffect, useMemo, useState } from 'react';
import { useOptionsByPathQuery } from '../../../../hooks/useOptionsByPathQuery.ts';

const UNKNOWN_ENRICHMENT_FEATURES_ERROR = /Unknown\s+enrichment_features/i;

export const useDemandPartnersFetch = (filters?: {
  account_uid?: string | null;
  enabled?: boolean;
}): WithRequired<OptionStateMap, 'refetch'> => {
  const enabled = filters?.enabled ?? true;
  const [includeRetargetingEnrichment, setIncludeRetargetingEnrichment] = useState(true);

  const filter = {
    ...(filters?.account_uid ? { account_uid: filters.account_uid } : {}),
    ...(includeRetargetingEnrichment ? { enrichment_features: 'retargeting' } : {}),
  };

  const {
    data: { optionsByPath = [] } = {},
    error,
    loading,
    refetch,
  } = useOptionsByPathQuery({
    skip: !enabled,
    variables: {
      path: 'deal.deal_participants.demand_partner',
      ...(Object.keys(filter).length ? { filter } : {}),
    },
  });

  useEffect(() => {
    if (includeRetargetingEnrichment && UNKNOWN_ENRICHMENT_FEATURES_ERROR.test(error?.message ?? '')) {
      setIncludeRetargetingEnrichment(false);
    }
  }, [error, includeRetargetingEnrichment]);

  const list = useMemo(() => {
    if (!optionsByPath) {
      return [];
    }

    return optionsByPath.map((option, i) => {
      if (option?.extra?.show_on_top !== undefined)
        return {
          id: option.id,
          name: option.name,
          ...option.extra,
          show_on_top: i,
        };

      return option
        ? {
            id: option.id,
            name: option.name,
            ...option.extra,
          }
        : [];
    });
  }, [optionsByPath]);

  const mapById = useMemo(() => {
    return list
      .map(option => ({ [option.id]: option }))
      .reduce((acc, curr) => {
        return Object.assign(acc, curr);
      }, {});
  }, [list]);

  return {
    loading,
    mapById,
    refetch,
  };
};
